/**
 * @fileoverview Account suggestion dialog component for order account management.
 * Provides functionality to suggest and select alternative accounts for orders
 * based on various criteria and business rules.
 *
 * @author Web Analysis Team
 * @version 1.0.0
 */

import * as React from 'react';
import MDialog from '../../../../../components/controls/MDialog';
import ClearIcon from '@mui/icons-material/Clear';
import { Box, Button, Grid, IconButton, InputAdornment, LinearProgress, TextField, Typography, useTheme } from '@mui/material';
import { DataGrid, useGridApiRef } from '@mui/x-data-grid';
import { formatDateTime, formatLocalTime } from '../../../../../components/ex_dayjs';
import MAutoComplete from '../../../../../components/controls/MAutoComplete';
import { useNotify } from 'react-admin';
import MNumber from '../../../../../components/controls/MNumber';
import { IData } from '../../../../../types';

/** Props for {@link SuggestAccountsPopup}. */
interface SuggestAccountsProps {
    /** Vendor configuration object. */
    vendor: any;
    /** Data provider for API operations. */
    dataProvider: any;
    /** Data containing order and dialog state information. */
    data: IData;
    /** Function to call when closing the dialog. */
    onClose: (...args: any[]) => void;
    /** Function to call when account selection changes. */
    onChange: (...args: any[]) => void;
}

/**
 * Account suggestion popup component for account selection and management.
 *
 * @param {Object} props - Component properties
 * @param {Object} props.vendor - Vendor configuration object
 * @param {Object} props.dataProvider - Data provider for API operations
 * @param {Object} props.data - Data containing order and dialog state information
 * @param {Object} props.data.order - Order object requiring account suggestion
 * @param {Object} props.data.dlgState - Dialog state with s_isOpen property
 * @param {Function} props.onClose - Function to call when closing the dialog
 * @param {Function} props.onChange - Function to call when account selection changes
 * @returns {JSX.Element|null} Account suggestion popup component
 */
const SuggestAccountsPopup: React.FC<SuggestAccountsProps> = ({ vendor, dataProvider, data, onClose, onChange }) => {
    const theme = useTheme();
    
    const { order } = data;
    const { s_isOpen } = data.dlgState;
    const [isLoading, setIsLoading] = React.useState(false);
    const [rows, setRows] = React.useState<any[]>([]);
    const [filteredRows, setFilteredRows] = React.useState<any[]>([]);
    const [rowSelected, setRowSelected] = React.useState<any>();
    const [isShowEventDate, setShowEventDate] = React.useState(false);

    /*
    Define threshold for today's order count based on event market
    - Exact: 15, AXS: 8, Others: 2
    */
    const mapThreshold = { exact: 15, axs: 8, other: 2 };
    const todayOrderCountThreshold = order ? (mapThreshold[order.event_market.toLowerCase()] || mapThreshold['other']) : 2;
    const [state, setState] = React.useState<IData>({
        ruleId: '',
        searchAcct: '',
        categorySelected: '',
        categories: [],
        todayOrderCountThreshold: todayOrderCountThreshold,
        minTickets: order.purchase_quantity || order.quantity
    });

    const apiRef = useGridApiRef();
    const searchAcctRef = React.useRef(null);
    const notify = useNotify();

    const initialState = {
        hideColumns: { eventDate: false }
    };

    React.useEffect(() => {
        if (s_isOpen && order) {
            setRows([]);
            setFilteredRows([]);
            setIsLoading(true);
            dataProvider.fetchData(vendor.controller, `SuggestAccounts?orderId=${order.id}`)
                .then((response: any) => {
                    let result = response.data;
                    if (result.success) {
                        if (result.data.some(e => e.eventDate)) {
                            apiRef.current.setColumnVisibilityModel({});
                            setShowEventDate(true);
                        }
                        setRows(result.data);
                        setFilteredRows(normalizeAccountList(result.data));
                    }
                    let categoryOptions = [...new Set(result.data.map((x: any) => x.category.trim().replace(/\r\n/g, '')))];
                    // main ruleId is the first one, but if there are other affected rules, we will show them as well
                    let affectedRules = [...new Set(result.data.flatMap((x: any) => x.affected_by_rules).filter((x: any) => x))].join(', ');
                    if (affectedRules && affectedRules !== result.ruleId) {
                        result.ruleId = affectedRules;
                    }
                    setState((prev) => ({ ...prev, ruleId: result.ruleId, categories: categoryOptions }));
                    setIsLoading(false);
                })
                .catch(err => {
                    notify(`Get sussgest accounts has error ${err}`, { type: 'error', autoHideDuration: 2000 });
                    setIsLoading(false);
                });
        }
    }, [s_isOpen, order]);

    const isModifiedWithinLast6Months = (modifiedDate) => {
        const sixMonthsAgo = new Date();
        sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
        return new Date(modifiedDate) >= sixMonthsAgo;
    };

    const columnDefs = [
        { field: 'primaryAccount', headerName: 'Primary Account', flex: 1.5,
            renderCell: record => {
                let modified = record.row.modified;
                // Highlight modified accounts in orange if modified 6 months recently
                if (!record.row.category?.includes('Special Rules') && modified && isModifiedWithinLast6Months(modified))
                    return <span style={{ color: 'orange', fontWeight: 'bold' }}>{record.value}</span>;
                return record.value;
            },
        },
        { field: 'account_State', headerName: 'State', flex: 0.5 },
        { field: 'category', headerName: 'Category', flex: 1 },
        { field: 'cC_Type', headerName: 'CC Type', flex: 0.6 },
        { field: 'account_Tags', headerName: 'Tags', flex: 0.6 },
        { field: 'eventDate', headerName: 'Event Date', valueFormatter: (value) => { return formatDateTime(value); }, flex: 1 },
        { field: 'totalTickets', headerName: 'Total Tickets', flex: 0.6, type: 'number' },
        { field: 'totalOrders', headerName: 'Total Orders', flex: 0.6, type: 'number', cellClassName: 'total-orders highlight' },
        { field: 'todayOrderCount', headerName: 'Today Order Count', flex: 0.8, type: 'number' },
        { field: 'limitTickets', headerName: 'Limit Tickets', flex: 0.6, type: 'number' },
        { field: 'limitOrders', headerName: 'Limit Orders', flex: 0.6, type: 'number' },
        { field: 'lastPurchase', headerName: 'Last Purchase', valueFormatter: (value) => { return !!value ? formatLocalTime(value) : null; }, flex: 1 },
    ];

    const closeDialog = () => onClose(null, 's_isOpen');

    const onCellClick = (params) => {
        setRowSelected(params.row);
    };

    // Both selection paths below always call onChange with the freshly selected row - including
    // when the same account is selected again - so the parent's PVC "resume with auto-pause" call
    // (order-details.js) fires on every selection with no extra plumbing needed here, letting the
    // backend's 10-minute auto-pause countdown reset automatically on re-selection.
    const onRowDoubleClick = (params) => {
        onChange(params.row);
        closeDialog();
    };

    const onSelect = () => {
        onChange(rowSelected);
        closeDialog();
    };

    const onSearchAccount = (e) => {
        const { value } = e.target;
        setState((prev) => ({ ...prev, searchAcct: value }));
    };

    const onChangeCategory = (value) => {
        setState((prev) => ({ ...prev, categorySelected: value }));
    };

    const onChangeTodayOrderCountThreshold = (value) => {
        setState((prev) => ({ ...prev, todayOrderCountThreshold: value }) );
    };

    const handleClear = () => {
        setState((prev) => ({ ...prev, searchAcct: '' }));
        if (searchAcctRef.current) {
            (searchAcctRef.current as any).querySelector('input').value = '';
        }
    };


    const onFilter = () => {
        let newSource = rows;
        if (state.searchAcct) {
            newSource = newSource.filter(e => e.primaryAccount.toLowerCase().includes(state.searchAcct.toLowerCase()));
        }
        if (state.categorySelected) {
            newSource = newSource.filter(e => e.category.includes(state.categorySelected));
        }
        if (state.todayOrderCountThreshold) {
            newSource = newSource.filter(e => e.todayOrderCount < state.todayOrderCountThreshold);
        }
        setFilteredRows(normalizeAccountList(newSource));
    };

    /**
     * Normalize the account list by adding expiration status and sorting
     * @param {*} rows
     * @returns {Array} Normalized and sorted account list
     */
    function normalizeAccountList (rows) {
        return rows.map(e => ({
            ...e,
            isExpired: e.limitTickets - e.totalTickets < state.minTickets
        })).sort((a, b) => {
            if (a.isExpired && !b.isExpired) return 1;
            if (!a.isExpired && b.isExpired) return -1;
            return 0;
        });
    }

    React.useEffect(() => {
        onFilter();
    }, [state.searchAcct, state.categorySelected]);

    return (
        <MDialog
            className={'order-dialog'}
            name={'s_isOpen'}
            open={s_isOpen}
            title={`Accounts Suggestion #${order?.order_number}`}
            handleClose={onClose}
            maxWidth="xl"
            height={800}
            action={<Button variant="contained" onClick={onSelect}>OK</Button>}
        >
            <Typography style={{ fontWeight: 'bold' }}>Applied by ruleId: {state.ruleId}</Typography>
            <Grid container spacing={2} sx={{ marginBottom: '5px' }}>
                <Grid size={6}>
                    <TextField
                        label={'Search by account'}
                        onKeyUp={onSearchAccount}
                        ref={searchAcctRef}
                        disabled={rows.length === 0}
                        slotProps={{
                            input: {
                                endAdornment: state.searchAcct ? (
                                    <InputAdornment position="end">
                                        <IconButton onClick={handleClear} edge="end">
                                            <ClearIcon style={{ fontSize: 'large' }} />
                                        </IconButton>
                                    </InputAdornment>
                                ) : null
                            }
                        }}
                    />
                </Grid>
                <Grid size={3} >
                    <MAutoComplete
                        label={'Category'}
                        choices={state.categories}
                        isMulti={false}
                        onChange={onChangeCategory}
                        disabled={rows.length === 0} />
                </Grid>
                <Grid size={3} sx={{ textAlign: 'right' }}>
                    <MNumber
                        // Limit to accounts with today order count less than the specified threshold
                        label={'Limit Today Order Count <'}
                        value={state.todayOrderCountThreshold}
                        min={1}
                        max={10}
                        disabled={true} // Currently disabled as per requirements
                        onChange={onChangeTodayOrderCountThreshold}
                        onKeyPress={onFilter}
                    />
                </Grid>
            </Grid>
            {isLoading && <LinearProgress color='secondary' />}
            <Box sx={{
                height: 550, width: '100%',
                ['.total-orders.highlight']: {
                    fontWeight: 'bold',
                    color: 'chocolate',
                },
                // Fix thoroughly the style for Expired Row
                '& .MuiDataGrid-row.expired-row': {
                    backgroundColor: theme.palette.mode === 'dark'
                        ? 'rgba(206, 60, 60, 0.35) !important' // Medium red highlight
                        : '#ffebee !important',

                    color: theme.palette.mode === 'dark' ? '#ffcdd2' : '#c62828',

                    // HOVER: Lighten slightly, not too dark
                    '&:hover': {
                        backgroundColor: theme.palette.mode === 'dark'
                            ? 'rgba(206, 60, 60, 0.2) !important' // Reduce alpha to 0.2 for a softer highlight
                            : '#ffcdd2 !important',               // Light mode brighter
                    },
                    '& .MuiDataGrid-cell': {
                        color: theme.palette.mode === 'dark' ? '#ff8a80' : '#b71c1c',
                    }
                },
            }}>
                <DataGrid
                    apiRef={apiRef}
                    rows={filteredRows}
                    columns={columnDefs as any}
                    density="compact"
                    getRowId={(x) => isShowEventDate ? x.primaryAccount + '_' + x.eventDate : x.primaryAccount}
                    getRowClassName={(params) => params.row.isExpired ? 'expired-row' : ''}
                    initialState={{
                        columns: {
                            columnVisibilityModel: initialState.hideColumns
                        },
                    }}
                    // SHOW NATIVE BROWSER TOOLTIP FOR EXPIRED ROWS (NO COLUMNDEFS CHANGES NEEDED)
                    slotProps={{
                        row: {
                            onMouseEnter: (event) => {
                                const rowId = event.currentTarget.getAttribute('data-id');
                                const rowData = filteredRows.find(r =>
                                    (isShowEventDate ? r.primaryAccount + '_' + r.eventDate : r.primaryAccount) === rowId
                                );
                                if (rowData && rowData.isExpired) {
                                    const remaining = rowData.limitTickets - rowData.totalTickets;
                                    const tooltipMsg = `Insufficient available tickets for this account (Remaining: ${remaining} | Required: ${state.minTickets})`;
                                    // Set the title directly on the Row DOM Element
                                    event.currentTarget.setAttribute('title', tooltipMsg);
                                    // Remove the default title from all child cells to avoid being overwritten by the number "7"
                                    const cells = event.currentTarget.querySelectorAll('.MuiDataGrid-cell');
                                    cells.forEach(cell => cell.removeAttribute('title'));
                                }
                            }
                        }
                    }}
                    pagination={undefined}
                    hideFooter={true}
                    onCellClick={onCellClick}
                    onRowDoubleClick={onRowDoubleClick}
                />
                {filteredRows.length && (
                    <Grid size={6} sx={{ textAlign: 'right', alignContent: 'end', paddingTop: '5px' }}>
                        <Typography style={{ fontWeight: 'bold' }}>Total Rows: {filteredRows.length}</Typography>
                    </Grid>
                )}
            </Box>
        </MDialog>
    );
};

export default React.memo(SuggestAccountsPopup);
